import { useCallback } from 'react';
import _ from 'lodash';
import { AllSecurityQuestionTypes } from '../types';
import {
  isPasswordValid,
  isUserNameContainedInPassword,
  doesPasswordContainForbiddenKeywords,
} from '@tradingblock/components';
import { UserRequirementsInfo } from '@tradingblock/types';

export const useFormHelpers = () => {
  const isPasswordValidBasic = useCallback((password: string): boolean => {
    return (
      password.length >= 8 &&
      /[a-z]/.test(password) &&
      /[A-Z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[!@#$%^&*(),.?":{}|<>]/.test(password)
    );
  }, []);

  const isPasswordValidComprehensive = useCallback(
    (
      password: string,
      userRequirements?: UserRequirementsInfo,
      userName?: string,
      firstName?: string,
      lastName?: string
    ): boolean => {
      // Use comprehensive validation if requirements are available
      if (userRequirements) {
        return isPasswordValid(password, userRequirements, userName);
      }

      // Fallback to basic validation
      if (!isPasswordValidBasic(password)) {
        return false;
      }

      // Additional checks
      if (userName && isUserNameContainedInPassword(password, userName)) {
        return false;
      }

      if (doesPasswordContainForbiddenKeywords(password)) {
        return false;
      }

      if (firstName && password && password.toLowerCase().includes(firstName.toLowerCase())) {
        return false;
      }

      if (lastName && password && password.toLowerCase().includes(lastName.toLowerCase())) {
        return false;
      }

      return true;
    },
    [isPasswordValidBasic]
  );

  const isUsernameValid = useCallback((username: string): boolean => {
    if (!username || username.length < 6 || username.length > 30) {
      return false;
    }
    // Only allow letters, numbers, underscores, and hyphens
    return /^[a-zA-Z0-9_-]+$/.test(username);
  }, []);

  const isEmailValid = useCallback((email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }, []);

  const isEmailConfirmed = useCallback((email: string, emailConfirm: string): boolean => {
    return Boolean(email && emailConfirm && email === emailConfirm);
  }, []);

  const getSecurityQuestionTypes = useCallback((values: any, index: number): string[] => {
    const otherValues = _.filter(values.securityChallenges, (_c: any, i: number) => i !== index).map(
      (c: any) => c.securityQuestionType
    );
    return _.filter(AllSecurityQuestionTypes, (t: string) => !_.includes(otherValues, t));
  }, []);

  const getFormProgress = useCallback((values: any) => {
    const requiredFields = [
      values.primaryAccountHolder && values.primaryAccountHolder.firstName,
      values.primaryAccountHolder && values.primaryAccountHolder.lastName,
      values.primaryAccountHolder && values.primaryAccountHolder.email,
      values.primaryAccountHolder && values.primaryAccountHolder.emailConfirm,
      values.userName,
      values.password,
      values.passwordConfirm,
      values.securityChallenges && values.securityChallenges[0].securityQuestionType,
      values.securityChallenges && values.securityChallenges[0].answer,
      values.securityChallenges && values.securityChallenges[1].securityQuestionType,
      values.securityChallenges && values.securityChallenges[1].answer,
      values.securityChallenges && values.securityChallenges[2].securityQuestionType,
      values.securityChallenges && values.securityChallenges[2].answer,
    ];

    const completedFields = requiredFields.filter(
      field => field && (typeof field === 'string' ? field.length > 0 : true)
    ).length;

    return {
      completed: completedFields,
      total: requiredFields.length,
      percentage: Math.min(100, (completedFields / requiredFields.length) * 100),
    };
  }, []);

  return {
    isPasswordValidBasic,
    isPasswordValidComprehensive,
    isUsernameValid,
    isEmailValid,
    isEmailConfirmed,
    getSecurityQuestionTypes,
    getFormProgress,
  };
};

export default useFormHelpers;
